/**
 * useKeyboardShortcuts · Global keyboard shortcut manager.
 *
 * Shortcuts implemented:
 *   Cmd/Ctrl+K → open CommandBar (existing; passed via callback)
 *   g h        → navigate to home (Hoje / cockpit)
 *   g p        → navigate to projects list
 *   g i        → navigate to inbox
 *   g n        → navigate to notifications
 *   ?          → open keyboard shortcuts help drawer
 *   Esc        → close any open overlay (passed via callback)
 *
 * "/" is intentionally left to InlineSearch / CommandBar — they register
 * their own handler so this hook doesn't double-fire.
 *
 * Gates to `pointer: fine` devices (g-sequences and ? don't make sense
 * on touch; Cmd+K is already in AppShell for all devices).
 *
 * Usage:
 *   const { shortcutsHelpOpen, setShortcutsHelpOpen } = useKeyboardShortcuts({
 *     onOpenCmdBar: () => setCmdBarOpen(true),
 *   });
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseKeyboardShortcutsOptions {
  /** Called when Cmd+K / Ctrl+K is pressed (AppShell already handles this;
   *  pass a no-op if you don't want double handling). */
  onOpenCmdBar?: () => void;
  /** Enable g-sequence shortcuts (default true). */
  enableGSequence?: boolean;
}

export interface UseKeyboardShortcutsResult {
  shortcutsHelpOpen: boolean;
  setShortcutsHelpOpen: (open: boolean) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useKeyboardShortcuts({
  onOpenCmdBar,
  enableGSequence = true,
}: UseKeyboardShortcutsOptions = {}): UseKeyboardShortcutsResult {
  const navigate = useNavigate();
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);

  // g-sequence buffer: after "g" is pressed we wait for the next key.
  const gBufferRef = useRef(false);
  const gTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearGBuffer = useCallback(() => {
    gBufferRef.current = false;
    if (gTimerRef.current) clearTimeout(gTimerRef.current);
  }, []);

  useEffect(() => {
    const isFinePointer =
      typeof window !== 'undefined' &&
      window.matchMedia('(pointer: fine)').matches;

    const handler = (e: KeyboardEvent) => {
      // Skip when typing in a text field.
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase() ?? '';
      const editable = (e.target as HTMLElement)?.isContentEditable;
      const inTextField =
        tag === 'input' || tag === 'textarea' || tag === 'select' || editable;

      // Cmd+K / Ctrl+K — handled by AppShell; we just forward.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        onOpenCmdBar?.();
        return;
      }

      // Skip remaining shortcuts if in a text field or non-fine pointer.
      if (inTextField) return;
      if (!isFinePointer) return;

      // "?" opens shortcuts help.
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setShortcutsHelpOpen((v) => !v);
        return;
      }

      if (!enableGSequence) return;

      // g-sequence: "g" then a letter.
      if (e.key === 'g' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        gBufferRef.current = true;
        // Clear after 1.5s if no follow-up key.
        if (gTimerRef.current) clearTimeout(gTimerRef.current);
        gTimerRef.current = setTimeout(clearGBuffer, 1500);
        return;
      }

      if (gBufferRef.current) {
        clearGBuffer();
        switch (e.key) {
          case 'h':
            e.preventDefault();
            navigate('/hoje');
            break;
          case 'p':
            e.preventDefault();
            navigate('/projects');
            break;
          case 'i':
            e.preventDefault();
            navigate('/inbox');
            break;
          case 'n':
            e.preventDefault();
            navigate('/notifications');
            break;
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      clearGBuffer();
    };
  }, [navigate, onOpenCmdBar, enableGSequence, clearGBuffer]);

  return { shortcutsHelpOpen, setShortcutsHelpOpen };
}
